import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/legacy/image";
import { SimpleGrid, Text, AspectRatio } from "@chakra-ui/react";
import {
  getPage,
  getSimpleIntegrations,
  getBlogs,
  getPlaybooks,
  urlFor,
  getSimpleCaseStudy,
  getFeaturedCustomers,
  getAnnouncementBanner,
  type IntegrationSimple,
  type Page as SanityPage,
  type Banner,
  type BlogSimple,
  type PlaybookSimple,
  type CaseStudySimple,
  type Customer,
} from "lib/sanity";
import { Section } from "components/section";
import { Page } from "components/page";
import { Quote } from "components/quotes";
import DestinationMarquee from "components/destination-marquee";
import { SectionHeading } from "components/section-heading";
import { Customers } from "components/logos";
import { HalfHero } from "components/half-hero";
import { ResourceCard } from "components/resource-card";
import { type CaseStudyCardProps, CaseStudyCard } from "components/solutions/marketing/case-study-card";
import { DemoEmbedHalf } from "components/demo-embed";
import { ArrowLink } from "ui/link";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import { CustomerStats } from "components/content";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";
import heroImage from "../../../public/assets/solutions/marketing/hero.png";
import fullWidthImage from "../../../public/assets/solutions/marketing/full-width.png";
import firstPairImage from "../../../public/assets/solutions/marketing/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/marketing/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/marketing/pair-3.png";

const featuredQuotes: CaseStudyCardProps[] = [
  {
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/390c5397fd12a45ff789cd04b79899cb387ea0c9-254x76.png",
    customerName: "Iterable",
    quote:
      "“The goal is to take that data that is collected and bridge the activation gap to create unforgettable customer experiences on an individual level.”",
    href: "https://iterable.com/blog/closing-the-activation-gap-embracing-customer-data/",
  },
  {
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/26e8c91c4bf5517e943ac123ccfb953ebc3a7824-3840x2160.png",
    customerName: "Meta",
    quote:
      "“When marketers can access data that inform the customers' unique interactions across the user journey, they can speak directly to customer needs at each lifecycle stage.”",
    href: "/blog/data-warehouse-unify",
  },
  {
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/cf4094a1d811d2b855b421ff49ba841c480fbe1b-2560x689.png",
    customerName: "Indeed",
    quote:
      "“Marketers need to understand their customers, activate data to engage them, and measure impact. The data warehouse as the source of truth makes this easy to do effectively.”",
    href: "/blog/breaking-data-silos",
  },
  {
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/85e881029dede095d87a1f3b1f7971f23451235f-216x216.png",
    customerName: "M1 Finance",
    quote:
      "“Using a data warehouse has allowed our marketers to access data more efficiently for segmentation and improve messaging consistency. Overall, a massive win for our teams.”",
    href: "/blog/data-warehouse-game-changer",
  },
  {
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/fe272a03f18b9ca32228b61baaf58b679aae92ad-228x228.png",
    customerName: "F5",
    quote:
      "“If you have a customer question to answer or a campaign goal to achieve, you can assemble the data in whichever way you need from a data warehouse. The flexibility is there unlike with other tools.”",
    href: "/blog/customer-data-insights",
  },
  {
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/cc40186101af4fed10d0599a6e72ae380cd804d2-349x155.png",
    customerName: "Clair",
    quote:
      "“Activating data from our warehouses to Hubspot means we're able to proactively create personalized experiences. It's saved both our marketing and data teams so much time and money!”",
  },
];

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  destinations: IntegrationSimple[];
  featuredStatCustomers: Customer[];
  featuredBlog?: BlogSimple;
  featuredPlaybook?: PlaybookSimple;
  featuredCaseStudy?: CaseStudySimple;
};

const Marketing: NextPage<Readonly<PageProps>> = ({
  page,
  announcementBanner,
  destinations,
  featuredStatCustomers,
  featuredBlog,
  featuredPlaybook,
  featuredCaseStudy,
}) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
      />

      {page.customers && <Customers title="Trusted by marketing teams at" customers={page.customers} />}

      <CustomerStats customers={featuredStatCustomers} />

      <Section center textAlign="center" maxW="4xl" mx="auto">
        <Text as="h2" textStyle="h4">
          You&apos;ve got a world-class MarTech stack. But do you have world-class data?
        </Text>

        <Text fontSize="lg" mt={8}>
          All of your customer data and more live in your data warehouse. Activate that data to enrich customer profiles
          and drive personalization across your marketing campaigns.
        </Text>

        <ArrowLink href="/blog/warehouse-for-marketers" variant="grass" size="lg" mt={8}>
          Learn more
        </ArrowLink>

        <AspectRatio ratio={2 / 1} pos="relative" w="100%" maxW="4xl" mx="auto" mt={{ base: 8, md: 16 }}>
          <Image
            src={fullWidthImage}
            alt="Groups of customer data of Taylor swift fans moving into various marketing tools and campaigns."
            objectFit="contain"
            layout="fill"
          />
        </AspectRatio>
      </Section>

      <DemoEmbedHalf
        title="Audience activation made easy"
        description={
          <Text>
            Hightouch syncs audiences from the data warehouse to all the tools your teams rely on. Just define an
            audience and start syncing. No CSVs required.
          </Text>
        }
      />

      <Section center>
        <SectionHeading title="The next wave of MarTech is powered by the cloud data warehouse" />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={{ base: 6, xl: 9 }} mt={{ base: 8, md: 12, lg: 16 }}>
          {featuredQuotes.map((quote) => (
            <CaseStudyCard key={quote.quote} {...quote} />
          ))}
        </SimpleGrid>
      </Section>

      <Section>
        <SectionHeading title="Advantages to embracing the cloud data warehouse with Hightouch" />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={firstPairImage}
          eyebrow="Completeness"
          title="Leverage all your customer data"
          actionText="Learn more"
          actionUrl="/platform/customer-studio"
          description={
            <Text>
              From web data to your latest data science models, use all the data in your cloud data warehouse for the
              most complete customer picture. Customer Studio lets any user explore, segment, and activate data to the
              downstream tools marketing teams rely on.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={secondPairImage}
          eyebrow="Flexibility"
          title="Create any campaign, powered with any data"
          actionText="Learn more"
          actionUrl="/blog/audience-builder-upgrade"
          description={
            <Text>
              Most applications, including legacy Customer Data Platforms (CDPs), are built around rigid data models
              that only support entities like users and events. With a warehouse-first approach to customer data, modern
              marketing teams activate data models that are built to fit any business need.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={thirdPairImage}
          eyebrow="Efficiency"
          title="Activate more from your marketing stack"
          actionText="Learn more"
          actionUrl="/platform/personalization-api"
          description={
            <Text>
              Do more with the marketing tools you’ve already invested in and rely on. You can enrich your targeting,
              positioning, and messaging with all of the data activated from your warehouse. You can even deliver
              real-time, personalized customer experiences with a low-latency API.
            </Text>
          }
        />
      </Section>

      <Quote
        size="sm"
        quote="If there’s been one monumental shift in the data landscape, it’s the rise of the data warehouse. The warehouse is the new source of truth for all of a business' data: customers, revenue, and inventory. I haven’t talked with any company operating at scale that doesn’t leverage a warehouse. It makes a ton of sense to base your business decisions off that source of truth, and that’s exactly what Hightouch is doing."
        author="Calvin French-Owen"
        job="Co-Founder"
        company="Segment"
        authorImage="https://cdn.sanity.io/images/pwmfmi47/production/de4f531fb33e250b9e8360f75d91824a0102a9fa-400x400.png"
      />

      <Section center bg="forest" mt={{ base: 20, md: 32 }}>
        <SectionHeading
          title="Resources"
          description="Learn how to leverage your data warehouse to unleash the power of your MarTech stack."
          color="white"
          w="100%"
        />

        <SimpleGrid mt={16} columns={{ base: 1, sm: 2, lg: 3 }} width="100%" gap={6}>
          {featuredBlog && (
            <ResourceCard
              href={`/blog/${featuredBlog.slug}`}
              linkAriaLabel={`Read ${featuredBlog.title}.`}
              title={featuredBlog.title}
              actionLabel="Read blog"
              image={
                <AspectRatio ratio={16 / 9} width="100%" display={["none", "block"]}>
                  <Image
                    src={urlFor(featuredBlog.image).width(600).url()}
                    alt={featuredBlog.title + "."}
                    layout="fill"
                    objectFit="cover"
                    placeholder="blur"
                    blurDataURL={blogBlurDataUrl}
                  />
                </AspectRatio>
              }
            />
          )}

          {featuredPlaybook && (
            <ResourceCard
              href={`/playbooks/${featuredPlaybook.slug}`}
              linkAriaLabel={`Read ${featuredPlaybook.title}.`}
              title={featuredPlaybook.title}
              actionLabel="Read playbook"
              image={
                <AspectRatio ratio={16 / 9} width="100%" display={["none", "block"]}>
                  <Image
                    src={featuredPlaybook.image ? urlFor(featuredPlaybook.image).width(600).url() : "/assets/meta.png"}
                    alt={featuredPlaybook.title + "."}
                    layout="fill"
                    objectFit="cover"
                    placeholder="blur"
                    blurDataURL={blogBlurDataUrl}
                  />
                </AspectRatio>
              }
            />
          )}

          {featuredCaseStudy && (
            <ResourceCard
              href={`/customers/${featuredCaseStudy.customerSlug}`}
              linkAriaLabel={`Read ${featuredCaseStudy.title}.`}
              title={featuredCaseStudy.title}
              actionLabel="Read case study"
              image={
                <AspectRatio ratio={16 / 9} width="100%" display={["none", "block"]}>
                  <Image
                    src={featuredCaseStudy.imageUrl ? featuredCaseStudy.imageUrl + "?w=600" : "/assets/meta.png"}
                    alt={featuredCaseStudy.title + "."}
                    layout="fill"
                    objectFit="cover"
                    placeholder="blur"
                    blurDataURL={blogBlurDataUrl}
                  />
                </AspectRatio>
              }
            />
          )}
        </SimpleGrid>
      </Section>

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [
    page,
    featuredStatCustomers,
    destinations,
    announcementBanner,
    featuredBlogs,
    featuredPlaybooks,
    featuredCaseStudy,
  ] = await Promise.all([
    getPage("/solutions/marketing"),
    getFeaturedCustomers(["lucidchart", "auto-trader", "gorgias"]),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getBlogs(["friends-dont-let-friends-buy-a-cdp"]),
    getPlaybooks(["Product-Utilization-Emails-Iterable"]),
    getSimpleCaseStudy("wmg"),
  ]);

  const featuredBlog = featuredBlogs[0];
  const featuredPlaybook = featuredPlaybooks[0];

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      featuredStatCustomers,
      announcementBanner,
      featuredBlog,
      featuredPlaybook,
      featuredCaseStudy,
    },
    revalidate: 60,
  };
};

export default Marketing;
